import React, { useState, forwardRef } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Button,
  Popover,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';

const DateRangeSelector = ({ selectedPeriod, onPeriodChange, dateRange, onCustomDateChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);

  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setTempStartDate(new Date(dateRange.startDate));
      setTempEndDate(new Date(dateRange.endDate));
      // Trigger popover on next tick after select closes
      setTimeout(() => {
        const selectElement = document.getElementById('date-range-select');
        if (selectElement) {
          setAnchorEl(selectElement);
        }
      }, 100);
    } else {
      onPeriodChange(value);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onCustomDateChange(
        dayjs(tempStartDate).format('YYYY-MM-DD'),
        dayjs(tempEndDate).format('YYYY-MM-DD')
      );
    }
    handleClose();
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const getDisplayText = () => {
    if (selectedPeriod === 'custom') {
      const start = dayjs(dateRange.startDate).format('MMM D, YYYY');
      const end = dayjs(dateRange.endDate).format('MMM D, YYYY');
      if (start === end) return start;
      return `${start} - ${end}`;
    }
    const labels = {
      '7days': 'Last 7 days',
      '28days': 'Last 28 days',
      '30days': 'Last 30 days',
      '90days': 'Last 90 days',
      '12months': 'Last 12 months',
      'ytd': 'Year to date',
      'lastyear': 'Last year',
      'alltime': 'All time',
    };
    return labels[selectedPeriod] || 'Last 30 days';
  };

  const open = Boolean(anchorEl);

  // Custom input for the date picker (not used directly but required by react-datepicker)
  const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <Button onClick={onClick} ref={ref} sx={{ display: 'none' }}>
      {value}
    </Button>
  ));

  return (
    <Box>
      <FormControl fullWidth>
        <Select
          id="date-range-select"
          value={selectedPeriod}
          onChange={handleSelectChange}
          startAdornment={
            <CalendarTodayIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />
          }
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            }
          }}
          renderValue={() => getDisplayText()}
        >
          <MenuItem value="7days">Last 7 days</MenuItem>
          <MenuItem value="28days">Last 28 days</MenuItem>
          <MenuItem value="30days">Last 30 days</MenuItem>
          <MenuItem value="90days">Last 90 days</MenuItem>
          <MenuItem value="12months">Last 12 months</MenuItem>
          <MenuItem value="ytd">Year to date</MenuItem>
          <MenuItem value="lastyear">Last year</MenuItem>
          <MenuItem value="alltime">All time</MenuItem>
          <Divider />
          <MenuItem value="custom">Custom range...</MenuItem>
        </Select>
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            mt: 1,
            overflow: 'visible',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <DatePicker
            selected={tempStartDate}
            onChange={handleDateChange}
            startDate={tempStartDate}
            endDate={tempEndDate}
            selectsRange
            inline
            monthsShown={2}
            maxDate={new Date()}
            calendarClassName="custom-datepicker"
          />
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'flex-end', 
            gap: 1, 
            mt: 2,
            pt: 2,
            borderTop: '1px solid #e5e7eb',
          }}>
              <Button
                variant="outlined"
              onClick={handleClose}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  color: '#6b7280',
                  borderColor: '#d1d5db',
                  px: 3,
                  '&:hover': {
                    borderColor: '#9ca3af',
                    bgcolor: 'transparent',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
              onClick={handleApply}
                disabled={!tempStartDate || !tempEndDate}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: '#111827',
                  px: 3,
                  '&:hover': { bgcolor: '#1f2937' },
                  '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
      </Popover>

      <style>
        {`
          .custom-datepicker {
            font-family: inherit;
            border: none;
          }
          .react-datepicker {
            border: none;
            font-family: inherit;
          }
          .react-datepicker__header {
            background: white;
            border-bottom: 1px solid #e5e7eb;
            padding-top: 12px;
          }
          .react-datepicker__current-month {
            font-weight: 600;
            font-size: 0.95rem;
            color: #1f2937;
            margin-bottom: 8px;
          }
          .react-datepicker__day-name {
            color: #9ca3af;
            font-weight: 500;
            width: 36px;
            line-height: 36px;
          }
          .react-datepicker__day {
            width: 36px;
            line-height: 36px;
            border-radius: 50%;
            color: #374151;
            transition: all 0.15s ease;
          }
          .react-datepicker__day:hover {
            background: #f3f4f6;
            border-radius: 50%;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--range-start,
          .react-datepicker__day--range-end {
            background: #3b82f6 !important;
            color: white !important;
            font-weight: 600;
          }
          .react-datepicker__day--in-range {
            background: #dbeafe;
            color: #1e40af;
            border-radius: 0;
          }
          .react-datepicker__day--in-selecting-range {
            background: #dbeafe;
            color: #1e40af;
          }
          .react-datepicker__day--range-start {
            border-radius: 50% 0 0 50% !important;
          }
          .react-datepicker__day--range-end {
            border-radius: 0 50% 50% 0 !important;
          }
          .react-datepicker__day--range-start.react-datepicker__day--range-end {
            border-radius: 50% !important;
          }
          .react-datepicker__day--disabled {
            color: #d1d5db;
          }
          .react-datepicker__day--outside-month {
            color: #d1d5db;
          }
          .react-datepicker__navigation {
            top: 12px;
          }
          .react-datepicker__navigation-icon::before {
            border-color: #6b7280;
          }
          .react-datepicker__month-container {
            padding: 0 8px;
          }
        `}
      </style>
    </Box>
  );
};

export default DateRangeSelector;
